


























































































































































import { Component, Vue, Watch, Mixins } from 'vue-property-decorator';
import CalendarEvent from '@/components/Calendar/CalendarEvent.vue';
import CalendarEventDot from '@/components/Calendar/CalendarEventDot.vue';
import CalendarBookingDot from '@/components/Calendar/CalendarBookingDot.vue';
import SingleCalendarEvent from '@/components/Calendar/SingleCalendarEvent.vue';
import CalendarEventData from '@/ts/CalendarEventData';
import CalendarEventList from '@/components/Calendar/CalendarEventList.vue';
import CalendarBookingList from '@/components/Calendar/CalendarBookingList.vue';
import { Mutations, Actions, Tabs, Views, ViewModes, Buttons } from '@/ts/store/Constants';
import DeviceMethods from '@/mixins/DeviceMethods.vue';
import moment from 'moment';

@Component({
    components: {
        CalendarEvent,
        CalendarEventList,
        SingleCalendarEvent,
        CalendarEventDot,
        CalendarBookingDot,
        CalendarBookingList,
    },
})
export default class Calendar extends Mixins(DeviceMethods) {
    // Class members
    private period: string = 'month';
    private today = this.$moment();
    private selectedDate = this.$moment();
    private headerDays: string[] = [];
    private singleStringHeaderDays: string[] = [];
    private weekDates: number[] = [];
    private unixMoment: number = 0;
    private daysPerMonth: number = 0;
    private calendarDays: any[] = [];
    private calendarEvents: { [index: string]: { [index: string]: CalendarEventData }; } = {};
    private tabKey: string = Tabs.Events.All;
    private currentViewMode: string = ViewModes.Events.All;
    private highlightedDate: string | null = null;


    // Lifetime hooks
    constructor() {
        super();

        this.$moment.locale('en-gb');

        this.$store.commit(Mutations.SET_LEFT_HEADER_BUTTON, Buttons.None);
        this.$store.commit(Mutations.SET_BOTTOM_MENU_VISIBILITY, true);
    }

    get activeEvent() {
        return this.$store.getters['Events/getActiveEvent'];
    }

    get view() {
        return this.$store.getters['Viewport/getCurrentView'];
    }

    get allEventsView() {
        return (this.view.viewMode === ViewModes.Events.All);
    }

    get singleEventView() {
        return (this.view.viewMode === ViewModes.Events.SingleEventView);
    }

    get eventListView() {
        return (this.view.viewMode === ViewModes.Events.EventListView);
    }

    get getEventData(): any {
        return this.$store.getters['Events/getEventData'];
    }

    get feedItemData(): any {
        return this.$store.getters['Notifications/getFeedItemData'];
    }

    get userFacilitiesBookings(): any {
        return this.$store.getters['SharedFacilities/getUserFacilitiesBookings'];
    }

    get sortedEventData(): CalendarEventData[] {
        let clone = this.filteredEventData.slice(0);

        if ((this.allEventsView || this.eventListView) && this.highlightedDate === null) {
            clone = clone.filter((item: CalendarEventData) => {
                return (item.eventTime.format('YYYYMMDD') > moment().format('YYYYMMDD'));
            });
        }

        // Return a time descending sorted version of the cloned array
        return clone.sort((firstItem: CalendarEventData, secondItem: CalendarEventData) => {
            if (firstItem.sortingCriterion > secondItem.sortingCriterion) {
                return 1;
            }

            if (firstItem.sortingCriterion < secondItem.sortingCriterion) {
                return -1;
            }

            return 0;
        });

        return clone;
    }

    get bookingData(): any[] {
        let clone = this.userFacilitiesBookings;

        clone = clone.filter((item: any) => {
            const start: any = moment(item.startTime);
            return (start.format('YYYYMMDD') === this.highlightedDate);
        });
        return clone;
    }

    get filteredEventData() {
        switch (this.tabKey) {
            case Tabs.Events.Important:
                return this.getEventData.filter((item: CalendarEventData) => {
                    if (this.highlightedDate === null) {
                        return (item.isImportant);
                    }

                    return (item.isImportant && item.eventTime.format('YYYYMMDD') === this.highlightedDate);
                });

                break;

            case Tabs.Events.NotImportant:
                return this.getEventData.filter((item: CalendarEventData) => {
                    if (this.highlightedDate === null) {
                        return (item.isImportant === false);
                    }

                    return (item.isImportant === false && item.eventTime.format('YYYYMMDD') === this.highlightedDate);
                });

                break;

            case Tabs.Events.All: // intentional fallthrough
            default:
                return this.getEventData.filter((item: CalendarEventData) => {
                    if (this.highlightedDate === null) {
                        return true;
                    }

                    return (item.eventTime.format('YYYYMMDD') === this.highlightedDate);
                });
        }
    }

    public dayClickHandler(day: string) {
        this.highlightedDate = day;
    }

    public tabClickHandler(event: MouseEvent) {
        const date = this.selectedDate;
        const interval = this.getMonthDateRange(date.year(), date.month() + 1);
        this.$store.dispatch(Actions.FETCH_NOTIFICATIONS, {type: 'event', interval});
        this.tabKey = (event.target as HTMLElement).dataset.tab;
    }

    public isActiveClass(elementTabKey: string) {
        return {
            'hococo-active-tab': (this.tabKey === elementTabKey),
            'hococo-inactive-tab': (this.tabKey !== elementTabKey),
        };
    }

    public beforeUpdate() {
        if (this.view.viewMode === ViewModes.Events.All) {
            this.$store.commit(Mutations.SET_RIGHT_HEADER_BUTTON, Buttons.EventListButton);
            this.$store.commit(Mutations.SET_HEADER_TEXT, '');
        }

        if (this.view.viewMode === ViewModes.Events.EventListView) {
            this.$store.commit(Mutations.SET_RIGHT_HEADER_BUTTON, Buttons.EventCalendarButton);
            this.$store.commit(Mutations.SET_HEADER_TEXT, Vue.i18n.translate('header.calendar', 'Calendar'));
            this.highlightedDate = null;
        }
    }

    public created() {
        const date = this.selectedDate;
        const interval = this.getMonthDateRange(date.year(), date.month() + 1);
        this.$store.commit(Mutations.SET_APP_DASHBOARD_NOTIFICATION_PAGE, 'all');
        this.$store.dispatch(Actions.FETCH_NOTIFICATIONS, {type: 'event', interval});
        this.$store.dispatch(Actions.FETCH_USER_FACILITIES_BOOKINGS);

        this.$store.commit(Mutations.SET_VIEW, {
            view: Views.Events,
            viewMode: ViewModes.Events.All,
        });
    }

    public resetSelection() {
        this.highlightedDate = null;
    }

    public getMonthDateRange(year: any, month: any) {
        const startDate = moment([year, month - 1]);
        const endDate = moment(startDate).endOf('month');
        return { start: startDate.format('YYYY-MM-DD'), end: endDate.format('YYYY-MM-DD') };
    }

    private mounted() {
        this.compileHeaderData();
        this.determineCalendarDays();
    }

    @Watch('unixMoment')
    private momentWatcher() {
        this.compileHeaderData();
    }

    @Watch('period')
    private periodWatcher() {
        this.compileHeaderData();
    }

    private isCurrentDayClass(day: string) {
        return {
            'hococo-current-day': (this.today.format('YYYYMMDD') === day),
        };
    }

    private isActiveDayClass(day: string) {
        return {
            'hococo-active-day': (this.highlightedDate === day),
        };
    }

    private isInactiveDayClass(activeMonth: boolean) {
        return {
            'hococo-inactive-day': !activeMonth,
        };
    }

    private getDayIndex(day: string): string {
        return day;
    }

    private getDailyEvents(day: number) {
        const dailyEvents = this.getEventData.filter((m: any) => {
            return (m.eventTime.format('YYYYMMDD') === day);
        });

        return dailyEvents;
    }

    private getDailyBookings(day: number) {
        const dailyBookings = this.userFacilitiesBookings.filter((m: any) => {
            const start: any = moment(m.startTime).format('YYYYMMDD');
            return (start === day);
        });

        return dailyBookings;
    }

    private compileHeaderData(): void {
        const localizationKey = this.$store.getters['Localization/getLocalizationKey'];

        this.headerDays = localizationKey === 'da'
            ? ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn']
            : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    }

    get sliderClass() {
        return {
            toggled: this.monthMode,
        };
    }

    get monthMode() {
        return (this.period === 'month');
    }

    get weekMode() {
        return (this.period === 'week');
    }

    private incrementDay() {
        this.selectedDate.add(1, 'day');
    }

    private decrementDay() {
        this.selectedDate.subtract(1, 'day');
    }

    private incrementWeek() {
        this.selectedDate.add(1, 'week');
    }

    private decrementWeek() {
        this.selectedDate.subtract(1, 'week');
    }

    private incrementMonth() {
        this.selectedDate.add(1, 'month');
    }

    private decrementMonth() {
        this.selectedDate.subtract(1, 'month');
    }

    private incrementYear() {
        this.selectedDate.add(1, 'year');
    }

    private decrementYear() {
        this.selectedDate.subtract(1, 'year');
    }

    private determineCalendarDays() {
        const month = this.selectedDate.month() + 1;
        const monthPart = (month < 10) ? '0' + month : month.toString();
        const year = this.selectedDate.year();
        const dayPart = '01';
        const firstOfMonthString = year.toString() + '-' + monthPart + '-' + dayPart;
        const firstDay = this.$moment(firstOfMonthString);

        let daysDifference = 0;

        const calendarDays = [];

        // Find first monday before calendar ends, set that as start date
        while (firstDay.weekday() !== 0) {
            firstDay.subtract(1, 'day');
            daysDifference++;
        }

        const totalDays = (daysDifference > 4) ? 42 : 35;

        while (calendarDays.length < totalDays) {
            calendarDays.push({
                day: firstDay.date(),
                month: firstDay.month(),
                year: firstDay.year(),
                key: firstDay.format('YYYYMMDD'),
                activeMonth: ((month - 1) === firstDay.month()),
            });
            firstDay.add(1, 'day');
        }

        this.calendarDays = calendarDays;
    }

    private goForward() {
        switch (this.period) {
            case 'week': this.incrementWeek(); break;
            case 'month': this.incrementMonth(); break;
            case 'year': this.incrementYear(); break;
        }

        // Forcing recomputation to avoid deep moment.js object inspection
        this.unixMoment = this.selectedDate.unix();
        this.determineCalendarDays();

        const date = this.selectedDate;
        const interval = this.getMonthDateRange(date.year(), date.month() + 1);
        this.$store.dispatch(Actions.FETCH_NOTIFICATIONS, {type: 'event', interval});
    }

    private goBack() {
        switch (this.period) {
            case 'week': this.decrementWeek(); break;
            case 'month': this.decrementMonth(); break;
            case 'year': this.decrementYear(); break;
        }

        // Forcing recomputation to avoid deep moment.js object inspection
        this.unixMoment = this.selectedDate.unix();
        this.determineCalendarDays();

        const date = this.selectedDate;
        const interval = this.getMonthDateRange(date.year(), date.month() + 1);
        this.$store.dispatch(Actions.FETCH_NOTIFICATIONS, {type: 'event', interval});
    }
}
